import type { CommentItem } from '@app/_proto/Protos/comments';
import { ReactComponent as ArrowUpIcon } from '@app/assets/arrow-up.svg';
import { useCommentsUrlHash } from '@app/domains/chapter/hooks/useCommentsUrlHash';
import CommentCard from '@app/domains/review/containers/CommentCard';
import { Collapse } from '@mui/material';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';

//#region : Main component

/** Custom. Main component parameters type */
type CommentProps = {
    entityId: number;
    comment: CommentItem;
    parentComment?: CommentItem;
    paragraphId?: string;
    visible?: boolean;
    replyDisabled?: boolean;
    replyDisabledMessage?: string;
    onReplySubmitted?: (id: number) => void;
    onReportComment?: (comment: CommentItem) => void;
} & JSX.IntrinsicElements['div'];

/** Custom. Handles display and actions of an individual comment. */

type MergedChildrenType = {
    comment: CommentItem;
    parentComment: CommentItem;
};

/** Custom. Handles display and actions of an individual comment and its children */
export default function CommentComponent({
    comment,
    entityId,
    parentComment,
    paragraphId,
    visible = true,
    replyDisabled,
    replyDisabledMessage,
    onReplySubmitted,
    onReportComment,
    ...rest
}: CommentProps) {
    //#region : Variables, functions and api calls

    const { hashCommentId, showHashCommentInThisPara } = useCommentsUrlHash(paragraphId);

    const mergedChildren = useMemo(() => {
        const flat = (v: CommentItem, p: CommentItem, arr: MergedChildrenType[]) => {
            arr.push({ comment: v, parentComment: p });
            (v.children || []).forEach(v2 => flat(v2, v, arr));
            return arr;
        };
        const allChildren = comment.children.reduce<MergedChildrenType[]>((arr, item) => flat(item, comment, arr), []);

        return allChildren;
    }, [comment]);

    const hasHashComment = useMemo(() => {
        return showHashCommentInThisPara && mergedChildren.some(c => c.comment.id === hashCommentId);
    }, [showHashCommentInThisPara, hashCommentId, mergedChildren]);

    //#region : Expand/Collapse comment's children
    const [expanded, setExpanded] = useState(hasHashComment);

    const onExpandComments = useCallback(() => {
        setExpanded(prev => !prev);
    }, []);

    /** When user replies to the parent comment, expand the replies. */
    const onParentCommentReply = useCallback(
        async (id: number) => {
            onReplySubmitted?.(id);
            setExpanded(true);
        },
        [onReplySubmitted]
    );

    //#endregion : Expand/Collapse comment's children

    //#endregion : Variables, functions and api calls

    return (
        <div id={`comment-${comment.id}${paragraphId ? `--${paragraphId}` : ''}`} {...rest}>
            <CommentCard
                comment={comment}
                entityId={entityId}
                parentComment={parentComment}
                paragraphId={paragraphId}
                onReplySubmitted={onParentCommentReply}
                onReportComment={onReportComment}
                visible={visible}
                replyDisabled={replyDisabled}
                replyDisabledMessage={replyDisabledMessage}
            />
            {mergedChildren.length > 0 && (
                <div
                    className="flex cursor-pointer items-center pt-4 pb-10 text-blue-600 sm:ml-80 sm:pb-16 sm:pt-0"
                    onClick={onExpandComments}
                >
                    <span className="font-set-sb14 sm:font-set-sb16">
                        {expanded ? 'Hide' : 'View'} replies ({mergedChildren.length})
                    </span>
                    {expanded ? (
                        <ArrowUpIcon className="ml-1 h-14 w-14" />
                    ) : (
                        <ArrowUpIcon className="ml-1 h-14 w-14 rotate-180" />
                    )}
                </div>
            )}
            <Collapse appear={false} in={expanded} timeout={250} mountOnEnter>
                <div className="ml-20 sm:ml-[70px]">
                    {mergedChildren.map(childComment => (
                        <CommentCard
                            key={childComment.comment.id}
                            visible={expanded}
                            replyDisabled={replyDisabled}
                            replyDisabledMessage={replyDisabledMessage}
                            entityId={entityId}
                            comment={childComment.comment}
                            parentComment={childComment.parentComment}
                            onReplySubmitted={onReplySubmitted}
                            onReportComment={onReportComment}
                            paragraphId={paragraphId}
                            id={`comment-${childComment.comment.id}${paragraphId ? `--${paragraphId}` : ''}`}
                            className={clsx({
                                'ml-20 sm:ml-[70px]': childComment.comment.depth >= 3,
                            })}
                        />
                    ))}
                </div>
            </Collapse>
        </div>
    );
}
